/**
 * Normalizes compromisso de compra e venda `formData` loaded from backend/Dynamo for editing:
 * - Unwraps `conjuge: { ... }` / `conjuge: null` into flat NOME_CONJUGE, CPF_CONJUGE, ...
 * - Migrates legacy vendedor/comprador field names to NOME_PESSOA_FISICA, etc.
 */

import { PESSOA_FISICA_TO_CORRETOR } from "./customerToRepeatablePessoaRow";

const CONJUGE_FIELD_IDS = [
  "NOME_CONJUGE",
  "CPF_CONJUGE",
  "RG_CONJUGE",
  "NACIONALIDADE_CONJUGE",
  "PROFISSAO_CONJUGE",
  "EMAIL_CONJUGE",
];

const OLD_VENDEDOR_KEYS = [
  "NOME_VENDEDOR",
  "CPF_V",
  "RG_V",
  "NACIONALIDADE_V",
  "PROFISSAO_V",
  "ESTADO_CIVIL_V",
  "RUA_ENDERECO_V",
  "N_ENDERECO_V",
  "BAIRRO_ENDERECO_V",
  "MUN_ENDERECO_V",
  "CEP_V",
  "EMAIL_VENDEDOR",
  "TEL_VENDEDOR",
];

const OLD_COMPRADOR_KEYS = [
  "NOME_COMPRADOR",
  "CPF_COMPRADOR",
  "RG_COMPRADOR",
  "NACIONALIDADEC",
  "PROF_COMPRADOR",
  "ESTADO_CIVILC",
  "RUA_ENDERECOC",
  "N_ENDERECOC",
  "BAIRRO_COMPRADOR",
  "MUN_COMPRADOR",
  "CEP_COMPRADOR",
  "EMAIL_COMPRADOR",
  "TEL_COMPRADOR",
];

/** Legacy `tipo_pagamento.fields` keys (lowercase) → current uppercase ids. */
const LEGACY_TIPO_PAGAMENTO_FIELD_KEYS = {
  valor_total: "VALOR_TOTAL",
  valor_entrada: "VALOR_ENTRADA",
  forma_pagamento: "FORMA_PAGAMENTO",
  data_pagamento: "DATA_PAGAMENTO",
  descricao_bem_permuta: "DESCRICAO_BEM_PERMUTA",
  quantidade_parcelas: "QUANTIDADE_PARCELAS",
  valor_parcela: "VALOR_PARCELA",
};

function migrateTipoPagamento(tp) {
  if (!tp || typeof tp !== "object" || !tp.fields) return tp;
  const f = tp.fields;
  const next = { ...f };
  let changed = false;
  Object.entries(LEGACY_TIPO_PAGAMENTO_FIELD_KEYS).forEach(([oldK, newK]) => {
    const hasOld = f[oldK] != null && f[oldK] !== "";
    const missingNew = f[newK] == null || f[newK] === "";
    if (hasOld && missingNew) {
      next[newK] = f[oldK];
      changed = true;
    }
  });
  return changed ? { ...tp, fields: next } : tp;
}

function normalizeEstadoCivilValue(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).toLowerCase().trim();
  if (s === "viuvo") return "viúvo";
  if (["solteiro", "casado", "divorciado", "viúvo"].includes(s)) return s;
  return String(raw);
}

function mapLegacyVendedorRow(o) {
  return {
    NOME_PESSOA_FISICA: o.NOME_VENDEDOR ?? "",
    CPF_PESSOA_FISICA: o.CPF_V ?? "",
    RG_PESSOA_FISICA: o.RG_V ?? "",
    NACIONALIDADE_PESSOA_FISICA: o.NACIONALIDADE_V ?? "",
    PROFISSAO_PESSOA_FISICA: o.PROFISSAO_V ?? "",
    ESTADO_CIVIL_PESSOA_FISICA: normalizeEstadoCivilValue(o.ESTADO_CIVIL_V),
    RUA_ENDERECO_PESSOA_FISICA: o.RUA_ENDERECO_V ?? "",
    N_ENDERECO_PESSOA_FISICA: o.N_ENDERECO_V ?? "",
    BAIRRO_ENDERECO_PESSOA_FISICA: o.BAIRRO_ENDERECO_V ?? "",
    MUN_ENDERECO_PESSOA_FISICA: o.MUN_ENDERECO_V ?? "",
    CEP_PESSOA_FISICA: o.CEP_V ?? "",
    EMAIL_PESSOA_FISICA: o.EMAIL_VENDEDOR ?? "",
    TEL_PESSOA_FISICA: o.TEL_VENDEDOR ?? "",
  };
}

function mapLegacyCompradorRow(o) {
  return {
    NOME_PESSOA_FISICA: o.NOME_COMPRADOR ?? "",
    CPF_PESSOA_FISICA: o.CPF_COMPRADOR ?? "",
    RG_PESSOA_FISICA: o.RG_COMPRADOR ?? "",
    NACIONALIDADE_PESSOA_FISICA: o.NACIONALIDADEC ?? "",
    PROFISSAO_PESSOA_FISICA: o.PROF_COMPRADOR ?? "",
    ESTADO_CIVIL_PESSOA_FISICA: normalizeEstadoCivilValue(o.ESTADO_CIVILC),
    RUA_ENDERECO_PESSOA_FISICA: o.RUA_ENDERECOC ?? "",
    N_ENDERECO_PESSOA_FISICA: o.N_ENDERECOC ?? "",
    BAIRRO_ENDERECO_PESSOA_FISICA: o.BAIRRO_COMPRADOR ?? "",
    MUN_ENDERECO_PESSOA_FISICA: o.MUN_COMPRADOR ?? "",
    CEP_PESSOA_FISICA: o.CEP_COMPRADOR ?? "",
    EMAIL_PESSOA_FISICA: o.EMAIL_COMPRADOR ?? "",
    TEL_PESSOA_FISICA: o.TEL_COMPRADOR ?? "",
  };
}

function stripKeys(obj, keys) {
  const out = { ...obj };
  keys.forEach((k) => {
    delete out[k];
  });
  return out;
}

/**
 * One vendedor or comprador row from API/Dynamo → flat shape expected by the form.
 */
export function normalizeCompromissoPessoaRow(row) {
  if (!row || typeof row !== "object") return row;

  let next = { ...row };

  if (Object.prototype.hasOwnProperty.call(next, "conjuge")) {
    const cj = next.conjuge;
    delete next.conjuge;
    if (cj && typeof cj === "object") {
      CONJUGE_FIELD_IDS.forEach((k) => {
        next[k] = cj[k] ?? "";
      });
    } else {
      CONJUGE_FIELD_IDS.forEach((k) => {
        if (next[k] == null) next[k] = "";
      });
    }
  }

  const hasNewPessoa =
    Object.prototype.hasOwnProperty.call(next, "NOME_PESSOA_FISICA");

  if (!hasNewPessoa && next.NOME_VENDEDOR !== undefined) {
    const mapped = mapLegacyVendedorRow(next);
    next = { ...stripKeys(next, [...OLD_VENDEDOR_KEYS, ...CONJUGE_FIELD_IDS]), ...mapped };
  } else if (!hasNewPessoa && next.NOME_COMPRADOR !== undefined) {
    const mapped = mapLegacyCompradorRow(next);
    next = { ...stripKeys(next, [...OLD_COMPRADOR_KEYS, ...CONJUGE_FIELD_IDS]), ...mapped };
  }

  CONJUGE_FIELD_IDS.forEach((k) => {
    if (next[k] == null) next[k] = "";
  });

  return next;
}

/**
 * Full `data` object from Dynamo/API → formData for compromisso-compra-venda edit.
 */
export function normalizeCompromissoLoadedFormData(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Array.isArray(out.vendedores)) {
    out.vendedores = out.vendedores.map(normalizeCompromissoPessoaRow);
  }
  if (Array.isArray(out.compradores)) {
    out.compradores = out.compradores.map(normalizeCompromissoPessoaRow);
  }
  if (out.tipo_pagamento) {
    out.tipo_pagamento = migrateTipoPagamento(out.tipo_pagamento);
  }
  return out;
}

export function normalizeContratoLocacaoLoadedFormData(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Array.isArray(out.locadores)) {
    out.locadores = out.locadores.map(normalizeCompromissoPessoaRow);
  }
  if (Array.isArray(out.locatarios)) {
    out.locatarios = out.locatarios.map(normalizeCompromissoPessoaRow);
  }
  const clauseType = out.clausula_garantia?.type;
  if (String(clauseType).toLowerCase() !== "fiador") {
    out.fiadores = [];
  } else if (Array.isArray(out.fiadores)) {
    out.fiadores = out.fiadores.map(normalizeCompromissoPessoaRow);
  }
  return out;
}

export const CORRETOR_ROW_FIELD_IDS = [
  "NOME_CORRETOR",
  "NACIONALIDADE_CORRETOR",
  "CPF_CORRETOR",
  "RG_CORRETOR",
  "CRECI_CORRETOR",
  "RUA_ENDERECO_CORRETOR",
  "N_ENDERECO_CORRETOR",
  "BAIRRO_ENDERECO_CORRETOR",
  "MUN_ENDERECO_CORRETOR",
  "CEP_CORRETOR",
  "EMAIL_CORRETOR",
  "TEL_CORRETOR",
];

/**
 * Migrates legacy pessoa física keys on a captante/intermediador row to corretor ids.
 */
export function migratePessoaRowToCorretor(row) {
  if (!row || typeof row !== "object") return row;
  const next = { ...row };
  Object.entries(PESSOA_FISICA_TO_CORRETOR).forEach(([pfKey, corKey]) => {
    if (next[corKey] == null || next[corKey] === "") {
      const legacy = next[pfKey];
      if (legacy != null && legacy !== "") {
        next[corKey] = legacy;
      }
    }
    delete next[pfKey];
  });
  delete next.PROFISSAO_PESSOA_FISICA;
  return next;
}

export function pickCorretorRowFields(row) {
  if (!row || typeof row !== "object") return row;
  const out = {};
  CORRETOR_ROW_FIELD_IDS.forEach((k) => {
    if (row[k] != null && row[k] !== undefined) {
      out[k] = row[k];
    }
  });
  return out;
}

/**
 * Strips conjuge-related keys from a pessoa row (used by contracts that don't
 * have conjuge fields, like contrato-parceria-vendas).
 */
function stripConjugeFromRow(row) {
  if (!row || typeof row !== "object") return row;
  const next = { ...row };
  if (Object.prototype.hasOwnProperty.call(next, "conjuge")) {
    delete next.conjuge;
  }
  CONJUGE_FIELD_IDS.forEach((k) => {
    delete next[k];
  });
  delete next.ESTADO_CIVIL_PESSOA_FISICA;
  return next;
}

function normalizeParceriaCorretorRow(row) {
  return pickCorretorRowFields(
    migratePessoaRowToCorretor(stripConjugeFromRow(row))
  );
}

export function normalizeParceriaVendasLoadedFormData(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Array.isArray(out.captantes)) {
    out.captantes = out.captantes
      .slice(0, 1)
      .map(normalizeParceriaCorretorRow);
  }
  if (Array.isArray(out.intermediadoras) && !Array.isArray(out.intermediadores)) {
    out.intermediadores = out.intermediadoras;
  }
  delete out.intermediadoras;
  if (Array.isArray(out.intermediadores)) {
    out.intermediadores = out.intermediadores
      .slice(0, 1)
      .map(normalizeParceriaCorretorRow);
  }
  return out;
}

export function normalizeAutorizacaoLocacaoLoadedFormData(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Array.isArray(out.proprietarios)) {
    out.proprietarios = out.proprietarios.map(normalizeCompromissoPessoaRow);
  }
  return out;
}
